import { Badge, Box, Button, Flex, Input, InputGroup, InputLeftElement, Portal, Tab, TabList, Tabs, TableContainer, Text } from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactSelect from "react-select";
import { RiSearchLine } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Layout from "../../../components/dashboard/Layout";
import LogoutMenu from "../../../components/dashboard/Layout/logout-menu";
import { adminSelectStyles } from "../../../components/Models/admin-modal-theme";
import { brandPalette } from "../../../constants/brandPalette";
import SubmissionsTable from "../../../containers/admin/correct-student-exams/submissions-table";
import CorrectExamModal from "../../../containers/admin/correct-student-exams/correct-exam-modal";
import PreviewCorrectionModal from "../../../containers/admin/correct-student-exams/preview-correction-modal";
import BulkGradeModal from "../../../containers/admin/correct-student-exams/bulk-grade-modal";
import BackgroundUploadsPanel from "../../../containers/admin/correct-student-exams/background-uploads-panel";
import { runCorrectionUpload } from "../../../containers/admin/correct-student-exams/background-correction-job";
import {
  TIMING_LEVELS,
  getSubmittedAt,
  getSubmissionTimingLevel,
  hasSubmittedPdf,
  isGraded,
} from "../../../containers/admin/correct-student-exams/submission-timing";
import { getAllExamsRequest } from "../../../modules/admin-modules/exams/Actions";
import { getStudentExamsByExamRequest, refuseStudentExamRequest } from "../../../modules/admin-modules/student-exams/Actions";
import { matchesStudentCodeSearch } from "../../../utils/studentCodeSearch";

const ASSIGNMENT_KINDS = [
  { id: "quiz", label: "Quiz", noun: "quiz", types: [0] },
  { id: "exam", label: "Exam", noun: "exam", types: [1, 6] },
  { id: "hw", label: "HW", noun: "homework", types: [2] },
];

const MAX_BULK_GRADE = 5;

const tabSx = {
  px: 4,
  py: 2,
  borderRadius: "full",
  fontWeight: "700",
  fontSize: "sm",
  borderWidth: "1px",
  borderColor: "var(--border-subtle)",
  bg: "var(--surface-muted)",
  color: "var(--text-muted)",
  _selected: {
    bg: brandPalette.primary,
    color: brandPalette.cream,
    borderColor: brandPalette.primary,
  },
  _hover: {
    borderColor: brandPalette.primary,
    color: "var(--text-primary)",
  },
};

const examSelectBase = adminSelectStyles({ menuZIndex: 1600 });

const selectStyles = {
  ...examSelectBase,
  menuPortal: (base) => ({ ...base, zIndex: 1600 }),
  control: (styles, state) => ({
    ...examSelectBase.control(styles, state),
    borderRadius: "14px",
    minHeight: "44px",
    minWidth: "280px",
  }),
};

const CorrectStudentExams = () => {
  const dispatch = useDispatch();
  const [assignmentKind, setAssignmentKind] = useState("quiz");
  const [examId, setExamId] = useState("");
  const [studentCodeSearch, setStudentCodeSearch] = useState("");
  /** @type {[Set<"fresh"|"aging"|"late">, Function]} */
  const [timingFilters, setTimingFilters] = useState(() => new Set());
  const [pendingOnly, setPendingOnly] = useState(false);
  const [refuseTarget, setRefuseTarget] = useState(null);
  const [selectedStudentExam, setSelectedStudentExam] = useState(null);
  const [correctModalOpen, setCorrectModalOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [selectedSubmissionIds, setSelectedSubmissionIds] = useState(() => new Set());
  const [bulkGradeOpen, setBulkGradeOpen] = useState(false);
  const [bulkReviewQueue, setBulkReviewQueue] = useState([]);
  const [bulkReviewIndex, setBulkReviewIndex] = useState(0);
  const [bulkMarkSchemeId, setBulkMarkSchemeId] = useState(null);
  const advancingBulkRef = useRef(false);
  const [uploadJobs, setUploadJobs] = useState([]);

  const exams = useSelector((state) => state.exams.exams);
  const examSubmissions = useSelector((state) => state.adminStudentExams.examSubmissions);
  const isLoading = useSelector((state) => state.adminStudentExams.isLoading);
  const isRefusingExam = useSelector((state) => state.adminStudentExams.isRefusingExam);

  useEffect(() => {
    dispatch(getAllExamsRequest());
  }, [dispatch]);

  useEffect(() => {
    if (!examId) return;
    dispatch(getStudentExamsByExamRequest(examId));
  }, [examId, dispatch]);

  useEffect(() => {
    setStudentCodeSearch("");
    setTimingFilters(new Set());
    setPendingOnly(false);
    setSelectedSubmissionIds(new Set());
    setBulkReviewQueue([]);
    setBulkReviewIndex(0);
    setBulkMarkSchemeId(null);
  }, [examId]);

  const currentKind =
    ASSIGNMENT_KINDS.find((kind) => kind.id === assignmentKind) ?? ASSIGNMENT_KINDS[0];
  const assignmentKindIndex = ASSIGNMENT_KINDS.findIndex((kind) => kind.id === currentKind.id);

  const examOptions = useMemo(
    () =>
      (exams || [])
        .filter((exam) => currentKind.types.includes(Number(exam?.type)))
        .map((exam) => ({
          value: exam.id,
          label: exam.name,
        })),
    [exams, currentKind]
  );

  const handleKindChange = (index) => {
    const next = ASSIGNMENT_KINDS[index];
    if (!next || next.id === assignmentKind) return;
    setAssignmentKind(next.id);
    setExamId("");
  };

  const selectedExamOption = useMemo(
    () => examOptions.find((opt) => String(opt.value) === String(examId)) ?? null,
    [examOptions, examId]
  );

  const hideCoverScore = useMemo(() => {
    const exam = (exams || []).find((item) => String(item.id) === String(examId));
    return Number(exam?.type) === 2;
  }, [exams, examId]);

  const submissionsWithPdf = useMemo(
    () => (examSubmissions || []).filter(hasSubmittedPdf),
    [examSubmissions]
  );

  const timingCounts = useMemo(() => {
    const counts = { fresh: 0, aging: 0, late: 0 };
    submissionsWithPdf.forEach((row) => {
      const level = getSubmissionTimingLevel(row);
      if (level) counts[level] += 1;
    });
    return counts;
  }, [submissionsWithPdf]);

  const pendingCount = useMemo(
    () => submissionsWithPdf.filter((row) => !isGraded(row)).length,
    [submissionsWithPdf]
  );

  const filteredSubmissions = useMemo(
    () =>
      submissionsWithPdf
        .filter((row) => {
          if (!matchesStudentCodeSearch(row?.student?.user_name, studentCodeSearch)) return false;
          if (pendingOnly && isGraded(row)) return false;
          if (timingFilters.size > 0) {
            const level = getSubmissionTimingLevel(row);
            if (!level || !timingFilters.has(level)) return false;
          }
          return true;
        })
        .sort((a, b) => new Date(getSubmittedAt(a)) - new Date(getSubmittedAt(b))),
    [submissionsWithPdf, studentCodeSearch, timingFilters, pendingOnly]
  );

  const toggleTimingFilter = (level) => {
    setTimingFilters((prev) => {
      const next = new Set(prev);
      if (next.has(level)) next.delete(level);
      else next.add(level);
      return next;
    });
  };

  const filtersActive = studentCodeSearch.trim().length > 0 || timingFilters.size > 0 || pendingOnly;

  const handleCorrect = (row) => {
    setSelectedStudentExam(row);
    setCorrectModalOpen(true);
  };

  const handlePreviewCorrection = (row) => {
    setSelectedStudentExam(row);
    setPreviewModalOpen(true);
  };

  const handleRefuse = (row) => {
    if (!row?.id) return;
    setRefuseTarget(row);
  };

  const closeRefuseConfirm = () => {
    if (isRefusingExam) return;
    setRefuseTarget(null);
  };

  const confirmRefuse = () => {
    if (!refuseTarget?.id) return;
    const refusedId = refuseTarget.id;
    dispatch(
      refuseStudentExamRequest(
        refusedId,
        () => {
          setRefuseTarget(null);
          if (selectedStudentExam?.id === refusedId) {
            setCorrectModalOpen(false);
            setSelectedStudentExam(null);
            setBulkReviewQueue([]);
            setBulkReviewIndex(0);
            setBulkMarkSchemeId(null);
          }
        },
        examId
      )
    );
  };

  const selectedSubmissions = useMemo(
    () => (examSubmissions || []).filter((row) => selectedSubmissionIds.has(row.id)),
    [examSubmissions, selectedSubmissionIds]
  );

  const currentBulkItem = bulkReviewQueue[bulkReviewIndex] || null;
  const isBulkReview = bulkReviewQueue.length > 0;

  const finishBulkReview = useCallback(
    (message) => {
      setCorrectModalOpen(false);
      setSelectedStudentExam(null);
      setBulkReviewQueue([]);
      setBulkReviewIndex(0);
      setBulkMarkSchemeId(null);
      setSelectedSubmissionIds(new Set());
      if (message) toast.success(message);
      if (examId) dispatch(getStudentExamsByExamRequest(examId));
    },
    [dispatch, examId]
  );

  const patchUploadJob = useCallback((id, patch) => {
    setUploadJobs((prev) => prev.map((job) => (job.id === id ? { ...job, ...patch } : job)));
  }, []);

  const startBackgroundUpload = useCallback(
    (snapshot) => {
      if (!snapshot?.id) return;
      setUploadJobs((prev) => {
        const without = prev.filter((item) => item.id !== snapshot.id);
        return [
          ...without,
          {
            id: snapshot.id,
            studentLabel: snapshot.studentLabel,
            status: snapshot.hasPdf ? "building" : "uploading",
            error: null,
            snapshot,
          },
        ];
      });

      (async () => {
        try {
          await runCorrectionUpload(snapshot, (status) => {
            patchUploadJob(snapshot.id, { status, error: null });
          });
          patchUploadJob(snapshot.id, { status: "done", error: null });
          if (snapshot.examId) dispatch(getStudentExamsByExamRequest(snapshot.examId));
        } catch (error) {
          const message =
            error?.response?.data?.message ||
            error?.response?.data?.data ||
            error?.message ||
            "Upload failed";
          patchUploadJob(snapshot.id, { status: "error", error: String(message) });
        }
      })();
    },
    [dispatch, patchUploadJob]
  );

  const handleRetryUpload = useCallback(
    (job) => {
      if (job?.snapshot) startBackgroundUpload(job.snapshot);
    },
    [startBackgroundUpload]
  );

  const handleClearFinishedUploads = useCallback(() => {
    setUploadJobs((prev) =>
      prev.filter((job) => job.status === "building" || job.status === "uploading")
    );
  }, []);

  const handleCloseCorrectModal = () => {
    if (advancingBulkRef.current) return;
    if (isBulkReview) {
      finishBulkReview();
      return;
    }
    setCorrectModalOpen(false);
    setSelectedStudentExam(null);
  };

  const handleClosePreviewModal = () => {
    setPreviewModalOpen(false);
    setSelectedStudentExam(null);
  };

  const handleToggleSelect = (row) => {
    setSelectedSubmissionIds((prev) => {
      const next = new Set(prev);
      if (next.has(row.id)) {
        next.delete(row.id);
        return next;
      }
      if (next.size >= MAX_BULK_GRADE) {
        toast.error(`You can select up to ${MAX_BULK_GRADE} submissions.`);
        return prev;
      }
      next.add(row.id);
      return next;
    });
  };

  const handleBulkGradeComplete = (results, markSchemeId) => {
    setBulkGradeOpen(false);
    setBulkMarkSchemeId(markSchemeId ?? null);
    setBulkReviewQueue(results || []);
    setBulkReviewIndex(0);
    if (results?.length) {
      setSelectedStudentExam(results[0].studentExam);
      setCorrectModalOpen(true);
    }
  };

  const advanceBulkReview = useCallback((fromSubmit = false) => {
    advancingBulkRef.current = true;
    const next = bulkReviewIndex + 1;
    if (next >= bulkReviewQueue.length) {
      finishBulkReview(
        fromSubmit
          ? "Review complete. Uploads continue in the background."
          : "Finished reviewing selected submissions."
      );
      advancingBulkRef.current = false;
      return;
    }
    setBulkReviewIndex(next);
    setSelectedStudentExam(bulkReviewQueue[next].studentExam);
    window.setTimeout(() => {
      advancingBulkRef.current = false;
    }, 0);
  }, [bulkReviewIndex, bulkReviewQueue, finishBulkReview]);

  return (
    <Layout>
      <Box p={{ base: 4, md: 6 }}>
        <Flex align="center" justify="space-between" mb={6} flexWrap="wrap" gap={4}>
          <Box>
            <Text fontSize="2xl" fontWeight="800" color="var(--text-primary)">
              Correct Student Assignments
            </Text>
            <Text fontSize="sm" color="var(--text-muted)" mt={1}>
              Review submitted PDFs and enter marks per question.
            </Text>
            <Text mt={1} fontSize="sm" fontWeight="700" color="var(--text-muted)">
              😊 &lt;12h · 😕 12–24h · 😠 &gt;24h since submit
            </Text>
          </Box>
          <LogoutMenu />
        </Flex>

        <Tabs
          index={assignmentKindIndex}
          onChange={handleKindChange}
          variant="unstyled"
          mb={6}
        >
          <TabList flexWrap="wrap" gap={2}>
            {ASSIGNMENT_KINDS.map((kind) => (
              <Tab key={kind.id} {...tabSx}>
                {kind.label}
              </Tab>
            ))}
          </TabList>
        </Tabs>

        <Flex
          mb={6}
          gap={4}
          flexWrap="wrap"
          align="flex-end"
        >
          <Box flex="1" minW={{ base: "100%", md: "280px" }}>
            <Text fontSize="xs" fontWeight="800" color="var(--text-muted)" textTransform="uppercase" mb={2}>
              Select {currentKind.noun}
            </Text>
            <ReactSelect
              options={examOptions}
              value={selectedExamOption}
              onChange={(opt) => {
                setExamId(opt?.value ?? "");
              }}
              styles={selectStyles}
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              placeholder={`Choose a ${currentKind.noun}…`}
              isClearable
            />
          </Box>

          {examId ? (
            <Box flex="1" minW={{ base: "100%", md: "240px" }}>
              <Text fontSize="xs" fontWeight="800" color="var(--text-muted)" textTransform="uppercase" mb={2}>
                Search by student code
              </Text>
              <InputGroup>
                <InputLeftElement pointerEvents="none" color="var(--text-muted)">
                  <RiSearchLine size={18} />
                </InputLeftElement>
                <Input
                  value={studentCodeSearch}
                  onChange={(e) => setStudentCodeSearch(e.target.value)}
                  placeholder="e.g. AQ-001 or AQ-001 to AQ-049"
                  bg="var(--surface-raised)"
                  borderColor="var(--border-subtle)"
                  borderRadius="14px"
                  pl={10}
                />
              </InputGroup>
            </Box>
          ) : null}
        </Flex>

        {examId ? (
          <Flex
            mb={4}
            gap={3}
            flexWrap="wrap"
            align={{ base: "stretch", md: "center" }}
          >
            <Flex align="center" gap={2} flexWrap="wrap">
              <Button
                type="button"
                size="sm"
                h="44px"
                px={3}
                borderRadius="14px"
                border="1px solid"
                borderColor={pendingOnly ? "orange.400" : "var(--border-subtle)"}
                bg={pendingOnly ? "rgba(237, 137, 54, 0.15)" : "var(--surface-muted)"}
                color={pendingOnly ? "orange.700" : "var(--text-primary)"}
                fontWeight="800"
                onClick={() => setPendingOnly((prev) => !prev)}
                _hover={{
                  bg: "rgba(237, 137, 54, 0.15)",
                  borderColor: "orange.400",
                }}
              >
                Pending
                <Badge
                  ml={2}
                  px={1.5}
                  py={0.5}
                  borderRadius="md"
                  fontWeight="700"
                  fontSize="xs"
                  bg={pendingOnly ? "whiteAlpha.800" : "blackAlpha.50"}
                  color="orange.700"
                >
                  {pendingCount}
                </Badge>
              </Button>
              {(["fresh", "aging", "late"]).map((level) => {
                const meta = TIMING_LEVELS[level];
                const active = timingFilters.has(level);
                const count = timingCounts[level];
                return (
                  <Button
                    key={level}
                    type="button"
                    size="sm"
                    h="44px"
                    px={3}
                    borderRadius="14px"
                    border="1px solid"
                    borderColor={active ? meta.color : "var(--border-subtle)"}
                    bg={active ? meta.bg : "var(--surface-muted)"}
                    color={active ? meta.color : "var(--text-primary)"}
                    fontWeight="800"
                    onClick={() => toggleTimingFilter(level)}
                    _hover={{
                      bg: meta.bg,
                      borderColor: meta.color,
                    }}
                  >
                    <Text as="span" fontSize="md" lineHeight={1} mr={1.5}>
                      {meta.face}
                    </Text>
                    {meta.label}
                    <Badge
                      ml={2}
                      px={1.5}
                      py={0.5}
                      borderRadius="md"
                      fontWeight="700"
                      fontSize="xs"
                      bg={active ? "whiteAlpha.800" : "blackAlpha.50"}
                      color={meta.color}
                    >
                      {count}
                    </Badge>
                  </Button>
                );
              })}
              {timingFilters.size > 0 || pendingOnly ? (
                <Button
                  type="button"
                  size="sm"
                  h="44px"
                  variant="ghost"
                  fontWeight="700"
                  color="var(--text-muted)"
                  onClick={() => {
                    setTimingFilters(new Set());
                    setPendingOnly(false);
                  }}
                >
                  Clear
                </Button>
              ) : null}
            </Flex>
            <Text fontSize="sm" fontWeight="600" color="var(--text-muted)" alignSelf="center">
              {filteredSubmissions.length} of {submissionsWithPdf.length} shown
              {selectedSubmissionIds.size === 0
                ? " · Select up to 5 to grade with AI"
                : ""}
            </Text>
            {selectedSubmissionIds.size > 0 ? (
              <Flex align="center" gap={3} ml={{ md: "auto" }} flexWrap="wrap">
                <Text fontSize="sm" fontWeight="700" color="var(--text-primary)">
                  {selectedSubmissionIds.size} of {MAX_BULK_GRADE} selected
                </Text>
                <Button
                  type="button"
                  size="sm"
                  h="44px"
                  variant="ghost"
                  fontWeight="700"
                  color="var(--text-muted)"
                  onClick={() => setSelectedSubmissionIds(new Set())}
                >
                  Clear selection
                </Button>
                <Button
                  type="button"
                  size="sm"
                  h="44px"
                  px={4}
                  borderRadius="14px"
                  bg={brandPalette.primary}
                  color="white"
                  fontWeight="800"
                  onClick={() => setBulkGradeOpen(true)}
                  _hover={{ bg: brandPalette.secondary }}
                >
                  Grade selected submissions
                </Button>
              </Flex>
            ) : null}
          </Flex>
        ) : null}

        {examId ? (
          <TableContainer
            borderWidth="1px"
            borderColor="var(--border-subtle)"
            borderRadius="xl"
            bg="var(--surface-raised)"
            p={4}
          >
            <SubmissionsTable
              rows={filteredSubmissions}
              isLoading={isLoading}
              onCorrect={handleCorrect}
              onPreviewCorrection={handlePreviewCorrection}
              onRefuse={handleRefuse}
              refusingId={isRefusingExam ? refuseTarget?.id : null}
              filtersActive={filtersActive}
              selectedIds={selectedSubmissionIds}
              onToggleSelect={handleToggleSelect}
              maxSelected={MAX_BULK_GRADE}
            />
          </TableContainer>
        ) : (
          <Text fontSize="sm" color="var(--text-muted)" py={8} textAlign="center">
            Select a {currentKind.noun} to view student submissions.
          </Text>
        )}
      </Box>

      <CorrectExamModal
        key={selectedStudentExam?.id || "correct"}
        isOpen={correctModalOpen}
        onClose={handleCloseCorrectModal}
        studentExam={selectedStudentExam}
        examId={examId}
        examName={selectedExamOption?.label}
        hideCoverScore={hideCoverScore}
        initialAiPayload={isBulkReview ? currentBulkItem?.aiPayload : null}
        initialMarkSchemeId={isBulkReview ? bulkMarkSchemeId : null}
        hideAiSection={isBulkReview}
        reviewIndex={isBulkReview ? bulkReviewIndex : null}
        reviewTotal={isBulkReview ? bulkReviewQueue.length : null}
        onSkip={isBulkReview ? () => advanceBulkReview(false) : null}
        onSubmitted={isBulkReview ? () => advanceBulkReview(true) : null}
        onBackgroundSubmit={isBulkReview ? startBackgroundUpload : null}
        onRefuse={handleRefuse}
        aiError={isBulkReview ? currentBulkItem?.error : null}
      />

      <PreviewCorrectionModal
        isOpen={previewModalOpen}
        onClose={handleClosePreviewModal}
        studentExam={selectedStudentExam}
      />

      <BulkGradeModal
        isOpen={bulkGradeOpen}
        onClose={() => setBulkGradeOpen(false)}
        submissions={selectedSubmissions}
        onComplete={handleBulkGradeComplete}
      />

      <BackgroundUploadsPanel
        jobs={uploadJobs}
        onRetry={handleRetryUpload}
        onClearDone={handleClearFinishedUploads}
      />

      {refuseTarget ? (
        <Portal>
          <Flex
            position="fixed"
            inset={0}
            zIndex={2000}
            align="center"
            justify="center"
            bg="blackAlpha.600"
            px={4}
            dir="ltr"
            onClick={closeRefuseConfirm}
          >
            <Box
              bg="var(--surface-raised)"
              color="var(--text-primary)"
              borderRadius="xl"
              boxShadow="lg"
              maxW="md"
              w="100%"
              p={6}
              onClick={(event) => event.stopPropagation()}
            >
              <Text fontWeight="800" fontSize="lg" mb={3}>
                Refuse submission
              </Text>
              <Text fontSize="sm" mb={6}>
                {`Refuse the submission from ${
                  refuseTarget?.student?.name || "this student"
                }${
                  refuseTarget?.student?.user_name ? ` (${refuseTarget.student.user_name})` : ""
                }? They will need to upload again.`}
              </Text>
              <Flex justify="flex-end" gap={3}>
                <Button
                  variant="ghost"
                  fontWeight="700"
                  onClick={closeRefuseConfirm}
                  isDisabled={isRefusingExam}
                >
                  Cancel
                </Button>
                <Button
                  colorScheme="red"
                  fontWeight="800"
                  onClick={confirmRefuse}
                  isLoading={isRefusingExam}
                >
                  Refuse
                </Button>
              </Flex>
            </Box>
          </Flex>
        </Portal>
      ) : null}
    </Layout>
  );
};

export default CorrectStudentExams;
